import { Delete, FileCopy, MoreVert } from '@mui/icons-material';
import {
    Alert, Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { DeleteConfirmationDialog } from '../common/DeleteConfirmationDialog';
import { PreviewGalleryButton } from './PreviewGalleryButton';

type SetDefinition = Models.SetsPlayground.SetDefinition;

interface SetsConfigTopBarActionsProps {
    productKey: string;
    previewCultures: string[];
    previewSets: SetDefinition[];
    onDelete: () => Promise<void>;
    onCopyFromProduct: () => void;
}

export const SetsConfigTopBarActions = ({
    productKey,
    previewCultures,
    previewSets,
    onDelete,
    onCopyFromProduct,
}: SetsConfigTopBarActionsProps): JSX.Element => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
        setMenuAnchorEl(null);
    };

    const handleCopyFromProductClick = (): void => {
        handleMenuClose();
        onCopyFromProduct();
    };

    const handleDeleteClick = (): void => {
        handleMenuClose();
        setDeleteError(null);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async (): Promise<void> => {
        try {
            await onDelete();
            setDeleteDialogOpen(false);
        } catch (err) {
            setDeleteError((err as Error).message);
        }
    };

    const handleDeleteCancel = (): void => {
        setDeleteDialogOpen(false);
        setDeleteError(null);
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <PreviewGalleryButton
                    cultures={previewCultures}
                    productKey={productKey}
                    sets={previewSets}
                />
                <Tooltip title="More actions">
                    <IconButton aria-label="sets config actions" onClick={handleMenuOpen}>
                        <MoreVert />
                    </IconButton>
                </Tooltip>
            </Box>
            <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleMenuClose}>
                <MenuItem onClick={handleCopyFromProductClick}>
                    <ListItemIcon>
                        <FileCopy fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        Copy from another product
                    </ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleDeleteClick}>
                    <ListItemIcon>
                        <Delete color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: 'error' }}>
                        Delete sets config
                    </ListItemText>
                </MenuItem>
            </Menu>
            <DeleteConfirmationDialog
                description="Are you sure you want to delete this sets config? This action cannot be undone."
                open={deleteDialogOpen}
                title="Delete Sets Config"
                onCancel={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
            >
                {deleteError && <Alert severity="error">{deleteError}</Alert>}
            </DeleteConfirmationDialog>
        </>
    );
};
